import * as React from 'react';
import { cn } from '../../lib/utils';
import { Karti } from '../brand/Karti';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const showToast = React.useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-24 left-4 right-4 z-[60] flex flex-col gap-3 sm:left-auto sm:max-w-md lg:bottom-6 lg:right-6">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const dot = {
    success: 'bg-correct',
    error: 'bg-wrong',
    info: 'bg-marker',
  };

  return (
    <div
      role="status"
      className={cn(
        'flex min-w-[260px] animate-slide-in-right items-center gap-3 rounded-list border-[3px] border-ink bg-cream p-3.5 text-ink shadow-hard-4'
      )}
    >
      {toast.type === 'success' ? (
        <Karti size={32} mood="happy" className="-my-1" />
      ) : (
        <span className={cn('h-3 w-3 shrink-0 rounded-full border-2 border-ink', dot[toast.type])} />
      )}
      <p className="flex-1 text-[15px] font-bold">{toast.message}</p>
      <button
        onClick={onClose}
        aria-label="Schließen"
        className="flex h-8 w-8 items-center justify-center rounded-tile border-2 border-ink bg-paper text-sm font-bold hover:bg-track"
      >
        ✕
      </button>
    </div>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
